import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { inventoryAPI, programsAPI } from '@/lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '@/components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Plus, ChevronLeft, ChevronRight, IndianRupee, LinkIcon, XCircle, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { EntityLink } from '@/components/EntityLink';
import { SearchableSelect } from '@/components/SearchableSelect';
import { useAuth } from '@/contexts/AuthContext';

const TXN_COLORS = {
  receive: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  issue: 'bg-red-50 text-red-700 border-red-200',
  return: 'bg-blue-50 text-blue-700 border-blue-200',
  return_to_supplier: 'bg-orange-50 text-orange-700 border-orange-200',
  regrind_send: 'bg-amber-50 text-amber-700 border-amber-200',
  regrind_receive: 'bg-teal-50 text-teal-700 border-teal-200',
  scrap: 'bg-slate-100 text-slate-600 border-slate-200',
  production_receive: 'bg-purple-50 text-purple-700 border-purple-200',
  transfer_in: 'bg-cyan-50 text-cyan-700 border-cyan-200',
  transfer_out: 'bg-rose-50 text-rose-700 border-rose-200',
  discrepancy_short: 'bg-red-50 text-red-700 border-red-200',
  discrepancy_excess: 'bg-emerald-50 text-emerald-700 border-emerald-200',
};

export default function TransactionListPage() {
  const navigate = useNavigate();
  const { isAdmin } = useAuth();
  const [searchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState('transactions');
  const [reverseTxn, setReverseTxn] = useState(null);
  const [reverseBusy, setReverseBusy] = useState(false);

  // ===== Transactions Tab =====
  const [transactions, setTransactions] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [typeFilter, setTypeFilter] = useState('');
  const [referenceFilter, setReferenceFilter] = useState(searchParams.get('reference') || '');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [page, setPage] = useState(1);
  const limit = 30;

  // ===== Misc Receipts Tab =====
  const [miscReceipts, setMiscReceipts] = useState([]);
  const [miscTotal, setMiscTotal] = useState(0);
  const [miscTotalSpend, setMiscTotalSpend] = useState(0);
  const [miscLoading, setMiscLoading] = useState(false);
  const [miscSearch, setMiscSearch] = useState('');
  const [miscPage, setMiscPage] = useState(1);

  // Link to Program dialog
  const [linkOpen, setLinkOpen] = useState(false);
  const [linkReceipt, setLinkReceipt] = useState(null);
  const [linkProgramId, setLinkProgramId] = useState('');
  const [programs, setPrograms] = useState([]);
  const [linkingProgram, setLinkingProgram] = useState(false);

  useEffect(() => { loadTransactions(); }, [page, typeFilter, referenceFilter]);
  useEffect(() => { if (activeTab === 'misc') loadMiscReceipts(); }, [activeTab, miscPage, miscSearch]);

  const loadTransactions = async () => {
    setLoading(true);
    try {
      const params = { page, limit };
      if (typeFilter) params.transaction_type = typeFilter;
      if (referenceFilter) params.reference_number = referenceFilter;
      if (fromDate) params.from_date = fromDate;
      if (toDate) params.to_date = toDate;
      const res = await inventoryAPI.listTransactions(params);
      setTransactions(res.data.transactions);
      setTotal(res.data.total);
    } catch {
      toast.error('Failed to load transactions');
    } finally { setLoading(false); }
  };

  const handleReverseTxn = async () => {
    if (!reverseTxn) return;
    setReverseBusy(true);
    try {
      const res = await inventoryAPI.reverseReceiveTxn(reverseTxn.id);
      toast.success(`Receipt reversed${res.data.po_status ? ` — PO is now ${(res.data.po_status || '').replace(/_/g, ' ')}` : ''}`);
      setReverseTxn(null);
      loadTransactions();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to reverse receipt');
    } finally { setReverseBusy(false); }
  };

  const loadMiscReceipts = async () => {
    setMiscLoading(true);
    try {
      const params = { page: miscPage, limit };
      if (miscSearch.trim()) params.search = miscSearch.trim();
      const res = await inventoryAPI.listMiscReceipts(params);
      setMiscReceipts(res.data.receipts || []);
      setMiscTotal(res.data.total || 0);
      setMiscTotalSpend(res.data.total_spend || 0);
    } catch {
      toast.error('Failed to load misc receipts');
    } finally { setMiscLoading(false); }
  };

  const openLinkDialog = async (receipt) => {
    setLinkReceipt(receipt);
    setLinkProgramId(receipt.program_id || '');
    if (programs.length === 0) {
      try {
        const res = await programsAPI.list();
        setPrograms(res.data || []);
      } catch { /* ignore */ }
    }
    setLinkOpen(true);
  };

  const handleLinkProgram = async () => {
    if (!linkReceipt) return;
    setLinkingProgram(true);
    try {
      await inventoryAPI.linkMiscReceiptProgram(linkReceipt.id, {
        program_id: linkProgramId === '_none' ? '' : linkProgramId,
      });
      toast.success('Program link updated');
      setLinkOpen(false);
      loadMiscReceipts();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to link program');
    } finally { setLinkingProgram(false); }
  };

  const totalPages = Math.ceil(total / limit);
  const miscTotalPages = Math.ceil(miscTotal / limit);

  return (
    <div className="space-y-4" data-testid="transaction-list-page">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
        <div>
          <h1 className="font-heading text-xl font-bold tracking-tight">Receive / Issue</h1>
          <p className="text-xs text-muted-foreground">{total} transactions{miscTotal > 0 && ` | ${miscTotal} misc receipts`}</p>
        </div>
        <Button size="sm" onClick={() => navigate('/inventory/transactions/new')} data-testid="new-transaction-btn">
          <Plus className="w-4 h-4 mr-1" /> New Transaction
        </Button>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="transactions" data-testid="tab-transactions">Inventory Transactions</TabsTrigger>
          <TabsTrigger value="misc" data-testid="tab-misc-receipts">
            Misc Receipts
            {miscTotal > 0 && <Badge className="ml-1.5 h-4 min-w-[18px] text-[10px] px-1 bg-blue-100 text-blue-700 border-blue-200">{miscTotal}</Badge>}
          </TabsTrigger>
        </TabsList>

        {/* ===== Inventory Transactions Tab ===== */}
        <TabsContent value="transactions" className="mt-4">
          {/* Filters */}
          <Card className="mb-3">
            <CardContent className="p-3 flex flex-col sm:flex-row gap-2">
              <Select value={typeFilter} onValueChange={v => { setTypeFilter(v === 'all' ? '' : v); setPage(1); }}>
                <SelectTrigger className="w-44 h-8 text-xs" data-testid="txn-type-filter">
                  <SelectValue placeholder="All Types" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Types</SelectItem>
                  <SelectItem value="receive">Receive</SelectItem>
                  <SelectItem value="issue">Issue</SelectItem>
                  <SelectItem value="return">Return</SelectItem>
                  <SelectItem value="return_to_supplier">Return to Supplier</SelectItem>
                  <SelectItem value="regrind_send">Regrind Send</SelectItem>
                  <SelectItem value="regrind_receive">Regrind Receive</SelectItem>
                  <SelectItem value="scrap">Scrap</SelectItem>
                  <SelectItem value="production_receive">Production Receive</SelectItem>
                </SelectContent>
              </Select>
              <Input type="date" value={fromDate} onChange={e => setFromDate(e.target.value)} className="h-8 text-xs w-36" placeholder="From" />
              <Input type="date" value={toDate} onChange={e => setToDate(e.target.value)} className="h-8 text-xs w-36" placeholder="To" />
              <Input
                value={referenceFilter}
                onChange={e => { setReferenceFilter(e.target.value); setPage(1); }}
                className="h-8 text-xs w-44"
                placeholder="Reference# (WO, Challan...)"
                data-testid="txn-reference-filter"
              />
              <Button size="sm" variant="secondary" className="h-8" onClick={() => { setPage(1); loadTransactions(); }} data-testid="apply-filter-btn">Apply</Button>
            </CardContent>
          </Card>

          {/* Transactions Table */}
          <Card>
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="transactions-table">
                <thead>
                  <tr className="border-b bg-muted/50">
                    <th className="text-left p-2.5 text-xs font-medium text-muted-foreground">Date</th>
                    <th className="text-left p-2.5 text-xs font-medium text-muted-foreground">Type</th>
                    <th className="text-left p-2.5 text-xs font-medium text-muted-foreground">Item</th>
                    <th className="text-left p-2.5 text-xs font-medium text-muted-foreground">Qty</th>
                    <th className="text-left p-2.5 text-xs font-medium text-muted-foreground">Godown</th>
                    <th className="text-left p-2.5 text-xs font-medium text-muted-foreground">Ref</th>
                    <th className="text-left p-2.5 text-xs font-medium text-muted-foreground">Trace #</th>
                    <th className="text-left p-2.5 text-xs font-medium text-muted-foreground">By</th>
                    {isAdmin && <th className="text-left p-2.5 text-xs font-medium text-muted-foreground">Actions</th>}
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr><td colSpan={9} className="text-center py-8 text-xs text-muted-foreground">Loading...</td></tr>
                  ) : transactions.length === 0 ? (
                    <tr><td colSpan={9} className="text-center py-8 text-xs text-muted-foreground">No transactions found</td></tr>
                  ) : transactions.map(txn => (
                    <tr key={txn.id} className="border-b hover:bg-muted/50 transition-colors" data-testid={`txn-row-${txn.id}`}>
                      <td className="p-2.5 font-mono text-xs">{txn.date}</td>
                      <td className="p-2.5">
                        <Badge variant="outline" className={`text-[10px] ${TXN_COLORS[txn.transaction_type] || ''}`}>
                          {txn.transaction_type.replace(/_/g, ' ')}
                        </Badge>
                      </td>
                      <td className="p-2.5">
                        <p className="text-xs font-medium"><EntityLink type="item" id={txn.item_id}>{txn.item_name}</EntityLink></p>
                        <p className="text-[10px] text-muted-foreground font-mono"><EntityLink type="item" id={txn.item_id} className="text-[10px] text-muted-foreground font-mono">{txn.item_unique_id}</EntityLink></p>
                      </td>
                      <td className="p-2.5 text-xs font-medium font-mono">
                        {['receive', 'return', 'regrind_receive', 'production_receive', 'transfer_in', 'discrepancy_excess', 'qc_accept_in', 'qc_quarantine_in', 'subcontract_receipt'].includes(txn.transaction_type)
                          ? <span className="text-emerald-600">+{txn.quantity}</span>
                          : ['issue', 'regrind_send', 'scrap', 'return_to_supplier', 'transfer_out', 'discrepancy_short', 'qc_accept_out', 'qc_quarantine_out', 'qc_reject_out', 'subcontract_send'].includes(txn.transaction_type)
                          ? <span className="text-red-600">-{txn.quantity}</span>
                          : txn.quantity}
                      </td>
                      <td className="p-2.5 text-xs">{txn.godown_name}</td>
                      <td className="p-2.5 text-xs text-muted-foreground">
                        {txn.reference_number || '-'}
                        {txn.transaction_type === 'return_to_supplier' && txn.supplier_name && (
                          <p className="text-[10px] text-orange-600 font-medium mt-0.5">Supplier: {txn.supplier_name}</p>
                        )}
                      </td>
                      <td className="p-2.5 text-xs">
                        {txn.traceability_id ? (
                          <span className="font-mono text-blue-700 font-medium">{txn.traceability_id}</span>
                        ) : txn.customer_wo_number ? (
                          <span className="font-mono text-blue-700 font-medium">{txn.customer_wo_number}</span>
                        ) : '-'}
                      </td>
                      <td className="p-2.5 text-xs text-muted-foreground">{txn.created_by_name}</td>
                      {isAdmin && (
                        <td className="p-2.5 text-xs">
                          {txn.transaction_type === 'receive' && txn.purchase_order_id && !txn.reversed ? (
                            <Button size="sm" variant="ghost" className="h-6 px-1.5 text-[10px] text-red-600 hover:bg-red-50"
                              onClick={() => setReverseTxn(txn)} data-testid={`reverse-txn-${txn.id}`}>
                              <XCircle className="w-3 h-3 mr-0.5" /> Reverse
                            </Button>
                          ) : txn.reversed ? (
                            <span className="text-[10px] text-muted-foreground italic">reversed</span>
                          ) : null}
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {totalPages > 1 && (
              <div className="flex items-center justify-between p-3 border-t">
                <p className="text-xs text-muted-foreground">Page {page} of {totalPages}</p>
                <div className="flex gap-1">
                  <Button size="sm" variant="outline" disabled={page <= 1} onClick={() => setPage(p => p - 1)} className="h-7 text-xs">
                    <ChevronLeft className="w-3 h-3 mr-1" /> Prev
                  </Button>
                  <Button size="sm" variant="outline" disabled={page >= totalPages} onClick={() => setPage(p => p + 1)} className="h-7 text-xs">
                    Next <ChevronRight className="w-3 h-3 ml-1" />
                  </Button>
                </div>
              </div>
            )}
          </Card>
        </TabsContent>

        {/* ===== Misc Receipts Tab ===== */}
        <TabsContent value="misc" className="mt-4">
          <Card className="mb-3 bg-blue-50/50 border-blue-200">
            <CardContent className="p-3 flex items-center justify-between">
              <div>
                <p className="text-xs text-blue-800">
                  <strong>Non-Inventory Spend Tracking:</strong> Miscellaneous receipts from NON-AS suppliers for generic items.
                  These are tracked for spend analytics only and do not affect inventory balances.
                </p>
              </div>
              <div className="text-right ml-4 flex-shrink-0">
                <p className="text-[10px] text-muted-foreground">Total Spend</p>
                <p className="text-sm font-bold text-blue-800 flex items-center gap-0.5">
                  <IndianRupee className="w-3.5 h-3.5" />
                  {miscTotalSpend.toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                </p>
              </div>
            </CardContent>
          </Card>

          <Card className="mb-3">
            <CardContent className="p-3 flex gap-2">
              <Input
                value={miscSearch}
                onChange={e => { setMiscSearch(e.target.value); setMiscPage(1); }}
                className="h-8 text-xs max-w-sm"
                placeholder="Search item, supplier, invoice..."
                data-testid="misc-search"
              />
            </CardContent>
          </Card>

          <Card>
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="misc-receipts-table">
                <thead>
                  <tr className="border-b bg-muted/50">
                    <th className="text-left p-2.5 text-xs font-medium text-muted-foreground">Date</th>
                    <th className="text-left p-2.5 text-xs font-medium text-muted-foreground">Item / Description</th>
                    <th className="text-left p-2.5 text-xs font-medium text-muted-foreground">Supplier</th>
                    <th className="text-left p-2.5 text-xs font-medium text-muted-foreground">Qty</th>
                    <th className="text-right p-2.5 text-xs font-medium text-muted-foreground">Value (INR)</th>
                    <th className="text-left p-2.5 text-xs font-medium text-muted-foreground">Invoice</th>
                    <th className="text-left p-2.5 text-xs font-medium text-muted-foreground">Program</th>
                    <th className="text-left p-2.5 text-xs font-medium text-muted-foreground">By</th>
                  </tr>
                </thead>
                <tbody>
                  {miscLoading ? (
                    <tr><td colSpan={8} className="text-center py-8 text-xs text-muted-foreground">Loading...</td></tr>
                  ) : miscReceipts.length === 0 ? (
                    <tr><td colSpan={8} className="text-center py-8 text-xs text-muted-foreground">No misc receipts found. Create one from New Transaction &rarr; Actual Receipt.</td></tr>
                  ) : miscReceipts.map(r => (
                    <tr key={r.id} className="border-b hover:bg-muted/50 transition-colors" data-testid={`misc-row-${r.id}`}>
                      <td className="p-2.5 font-mono text-xs">{r.date}</td>
                      <td className="p-2.5">
                        <p className="text-xs font-medium">{r.item_name}</p>
                        {r.description && r.description !== r.item_name && (
                          <p className="text-[10px] text-muted-foreground truncate max-w-[200px]">{r.description}</p>
                        )}
                        <p className="text-[10px] text-muted-foreground font-mono">{r.item_code}</p>
                      </td>
                      <td className="p-2.5 text-xs">{r.supplier_name}</td>
                      <td className="p-2.5 text-xs font-mono">{r.quantity} {r.unit_of_measure}</td>
                      <td className="p-2.5 text-xs font-mono text-right font-medium">
                        {r.total_value?.toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                        {r.cost_per_item > 0 && (
                          <p className="text-[10px] text-muted-foreground">@{r.cost_per_item?.toFixed(2)}/unit</p>
                        )}
                      </td>
                      <td className="p-2.5 text-xs text-muted-foreground">{r.reference_number || '-'}</td>
                      <td className="p-2.5 text-xs">
                        {r.program_name ? (
                          <Badge variant="outline" className="text-[10px] bg-purple-50 text-purple-700 border-purple-200">{r.program_name}</Badge>
                        ) : (
                          <Button size="sm" variant="ghost" className="h-6 text-[10px] text-blue-600" onClick={() => openLinkDialog(r)} data-testid={`link-program-${r.id}`}>
                            <LinkIcon className="w-3 h-3 mr-0.5" /> Link
                          </Button>
                        )}
                      </td>
                      <td className="p-2.5 text-xs text-muted-foreground">{r.created_by_name}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {miscTotalPages > 1 && (
              <div className="flex items-center justify-between p-3 border-t">
                <p className="text-xs text-muted-foreground">Page {miscPage} of {miscTotalPages}</p>
                <div className="flex gap-1">
                  <Button size="sm" variant="outline" disabled={miscPage <= 1} onClick={() => setMiscPage(p => p - 1)} className="h-7 text-xs">
                    <ChevronLeft className="w-3 h-3 mr-1" /> Prev
                  </Button>
                  <Button size="sm" variant="outline" disabled={miscPage >= miscTotalPages} onClick={() => setMiscPage(p => p + 1)} className="h-7 text-xs">
                    Next <ChevronRight className="w-3 h-3 ml-1" />
                  </Button>
                </div>
              </div>
            )}
          </Card>
        </TabsContent>
      </Tabs>

      {/* Link to Program Dialog */}
      <Dialog open={linkOpen} onOpenChange={setLinkOpen}>
        <DialogContent className="max-w-md" data-testid="link-program-dialog">
          <DialogHeader>
            <DialogTitle className="text-sm">Link to Program</DialogTitle>
          </DialogHeader>
          {linkReceipt && (
            <div className="space-y-3">
              <div className="text-xs text-muted-foreground p-2 bg-slate-50 rounded">
                <p><strong>{linkReceipt.item_name}</strong> - INR {linkReceipt.total_value?.toFixed(2)}</p>
                <p>Supplier: {linkReceipt.supplier_name} | {linkReceipt.date}</p>
              </div>
              <div>
                <Label className="text-xs">Program</Label>
                <SearchableSelect
                  options={[
                    { value: '_none', label: 'No program (unlink)', sub: '', searchStr: 'none unlink' },
                    ...programs.map(p => ({
                      value: p.id,
                      label: p.program_name || p.program_number,
                      sub: p.program_number || '',
                      searchStr: `${p.program_name} ${p.program_number || ''}`,
                    }))
                  ]}
                  value={linkProgramId || '_none'}
                  onSelect={v => setLinkProgramId(v === '_none' ? '' : v)}
                  placeholder="Search programs..."
                  emptyText="No programs found."
                  testId="link-program-select"
                />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" size="sm" onClick={() => setLinkOpen(false)}>Cancel</Button>
            <Button size="sm" onClick={handleLinkProgram} disabled={linkingProgram} data-testid="link-program-confirm">
              {linkingProgram ? 'Saving...' : 'Save'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reverse receive txn confirm */}
      <Dialog open={!!reverseTxn} onOpenChange={() => setReverseTxn(null)}>
        <DialogContent className="max-w-sm" data-testid="reverse-txn-dialog">
          <DialogHeader><DialogTitle className="text-sm text-red-700 flex items-center gap-2"><XCircle className="w-4 h-4" /> Reverse goods receipt</DialogTitle></DialogHeader>
          {reverseTxn && (
            <p className="text-xs text-muted-foreground">
              This will void the receipt of <strong>{reverseTxn.quantity} × {reverseTxn.item_name}</strong> (Ref {reverseTxn.reference_number || '—'}),
              remove that stock from <strong>{reverseTxn.godown_name}</strong>, and reopen the pending quantity on the linked PO.
              Blocked if the stock has already been issued, consumed or dispatched.
            </p>
          )}
          <DialogFooter>
            <Button variant="outline" size="sm" onClick={() => setReverseTxn(null)}>Cancel</Button>
            <Button size="sm" className="bg-red-600 hover:bg-red-700" onClick={handleReverseTxn} disabled={reverseBusy} data-testid="confirm-reverse-txn">
              {reverseBusy && <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" />} Reverse receipt
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
